import React, { useState, useRef } from 'react';

/**
 * TagInput — multi-value chip input
 *
 * Props:
 *   value       string   — comma-separated string (synced to DB format)
 *   onChange    fn(str)  — called with updated comma-separated string
 *   suggestions []str    — optional dropdown suggestions
 *   placeholder string
 *   color       string   — accent color for chips (default purple)
 */
export default function TagInput({ value = '', onChange, suggestions = [], placeholder = 'Type and press Enter...', color = '#E55D4A' }) {
  const [input, setInput]       = useState('');
  const [focused, setFocused]   = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const inputRef = useRef(null);

  // Parse current comma-separated string → tag array
  const tags = value
    ? value.split(',').map(s => s.trim()).filter(Boolean)
    : [];

  // Filter suggestions: not already added, matches typed input
  const filtered = input.trim().length > 0
    ? suggestions.filter(s =>
        s.toLowerCase().includes(input.toLowerCase()) &&
        !tags.map(t => t.toLowerCase()).includes(s.toLowerCase())
      )
    : suggestions.filter(s => !tags.map(t => t.toLowerCase()).includes(s.toLowerCase()));

  const showDropdown = focused && (filtered.length > 0);

  function addTag(raw) {
    const val = raw.trim();
    if (!val) return;
    if (tags.map(t => t.toLowerCase()).includes(val.toLowerCase())) {
      setInput(''); return;
    }
    const next = [...tags, val];
    onChange(next.join(', '));
    setInput('');
    setHighlighted(-1);
  }

  function removeTag(idx) {
    const next = tags.filter((_, i) => i !== idx);
    onChange(next.join(', '));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (highlighted >= 0 && filtered[highlighted]) {
        addTag(filtered[highlighted]);
      } else if (input.trim()) {
        addTag(input);
      }
    } else if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      removeTag(tags.length - 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, -1));
    } else if (e.key === 'Escape') {
      setFocused(false);
    }
  }

  const wrap = {
    background: 'rgba(255,255,255,0.05)',
    border: `0.5px solid ${focused ? color : 'rgba(245,235,221,0.12)'}`,
    borderRadius: 8,
    padding: '6px 10px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
    cursor: 'text',
    minHeight: 42,
    transition: 'border-color 0.15s',
    position: 'relative',
  };

  const chip = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: `${color}22`,
    border: `0.5px solid ${color}55`,
    color: color,
    borderRadius: 20,
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  };

  const inp = {
    background: 'none',
    border: 'none',
    outline: 'none',
    color: '#F5EBDD',
    fontSize: 13,
    fontFamily: 'inherit',
    flex: 1,
    minWidth: 100,
    padding: '2px 0',
  };

  const dropdown = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: '#2B2B1F',
    border: '0.5px solid rgba(245,235,221,0.12)',
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 180,
    overflowY: 'auto',
    zIndex: 100,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={wrap} onClick={() => inputRef.current?.focus()}>
        {tags.map((tag, i) => (
          <span key={i} style={chip}>
            {tag}
            <span
              onClick={e => { e.stopPropagation(); removeTag(i); }}
              style={{ cursor: 'pointer', opacity: 0.6, fontSize: 14, lineHeight: 1, marginLeft: 1 }}
            >×</span>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={e => { setInput(e.target.value); setHighlighted(-1); }}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={tags.length === 0 ? placeholder : ''}
          style={inp}
        />
      </div>
      {showDropdown && (
        <div style={dropdown}>
          {filtered.map((s, i) => (
            <div
              key={s}
              onMouseDown={() => addTag(s)}
              style={{
                padding: '8px 14px',
                fontSize: 13,
                cursor: 'pointer',
                background: i === highlighted ? `${color}22` : 'transparent',
                color: i === highlighted ? color : 'rgba(245,235,221,0.75)',
              }}
            >{s}</div>
          ))}
        </div>
      )}
      {tags.length === 0 && (
        <div style={{ fontSize: 10, color: 'rgba(245,235,221,0.2)', marginTop: 4 }}>
          Press Enter or comma to add multiple values
        </div>
      )}
    </div>
  );
}
